import React, { useContext } from 'react';
import { AuthContext } from './AuthContext';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Formatter = (value: any) => string; // TODO: support ReactNode here

const defaultFormatters: Record<string, Formatter> = {
  string: (value: string) => value,
  number: (value: number) => value.toString(),
  undefined: () => '',
  null: () => '',
};

export interface ColumnDefinition {
  title: string;
  index: number;
  formatter?: Formatter;
}

export type ActionType<R> = [
  icon: JSX.Element,
  actionFunction: (token: string, row: R) => Promise<void>,
];

type CellValueType = string | number | null | boolean | object;

export class RowType {
  [key: string]: CellValueType;
}

export type GenericTableProps<R extends RowType> = {
  rows: R[];
  listOfActions?: ActionType<R>[];
};

export function GenericTable<R extends RowType>(
  columnDefs: Record<string, ColumnDefinition>,
): React.FC<GenericTableProps<R>> {
  const columnProjection = (columnDefs: Record<string, ColumnDefinition>) => {
    const cols: [string, ColumnDefinition][] = Object.entries(columnDefs);
    cols.sort(
      (a: [string, ColumnDefinition], b: [string, ColumnDefinition]) =>
        a[1].index - b[1].index,
    );
    const actionsColumn: [string, ColumnDefinition] = [
      'actions',
      { title: 'Actions', index: 9999 },
    ];

    const result: [string, ColumnDefinition][] = cols;

    // add actions column
    result.push(actionsColumn);

    return result;
  };
  const rowProjection = (row: R) => {
    // filter out columns that are not in the columns parameter
    const entries: [string, CellValueType][] = Object.entries(row).filter(
      ([key]) => columnDefs[key],
    );
    entries.sort(
      (a: [string, CellValueType], b: [string, CellValueType]) =>
        columnDefs[a[0]].index - columnDefs[b[0]].index,
    );
    return entries;
  };

  const GenericTableFunction = ({
    rows,
    listOfActions,
  }: GenericTableProps<R>) => {
    const authContext = useContext(AuthContext);
    const { token } = authContext;

    return (
      <div className="overflow-x-auto">
        <table className="table table-zebra">
          <thead>
            <tr>
              {columnProjection(columnDefs).map(([key, column]) => (
                <th key={key} className="text-center">
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows &&
              rows.map((row: R, i: number) => (
                <tr key={i}>
                  {rowProjection(row).map(([key, value]) => {
                    const column = columnDefs[key];
                    const formatter =
                      column.formatter || defaultFormatters[typeof value];
                    const formatted = formatter(value);
                    return <td key={key}>{formatted}</td>;
                  })}

                  <td key={`actions-column_${i}`} className="text-center">
                    <div className="flex items-center justify-center space-x-4">
                      {listOfActions &&
                        listOfActions.map(
                          ([icon, action]: ActionType<R>, j: number) => (
                            <button
                              key={`actions-button_${i}_${j}`}
                              className="inline-flex items-center justify-center"
                              onClick={() => {
                                action(token, row);
                              }}
                              data-testid={`actions-button_${i}_${j}`}
                            >
                              {icon}
                            </button>
                          ),
                        )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  };
  return GenericTableFunction;
}
